package or.garden.manager.features.beds.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import or.garden.manager.domain.model.BedData
import or.garden.manager.domain.model.CropData
import or.garden.manager.domain.model.PlantedCrop
import or.garden.manager.domain.model.PlantingSuggestionsResponse
import or.garden.manager.ui.utils.getCropImageSource
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.left
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.top
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.I
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun BedPlannerView(
    bed: BedData,
    plantedCrops: List<PlantedCrop>,
    availableCrops: List<CropData>,
    isLoading: Boolean,
    isSaving: Boolean,
    aiSuggestions: PlantingSuggestionsResponse? = null,
    onBack: () -> Unit,
    onSave: () -> Unit,
    onAddCrop: (CropData, Int, Int, Int?) -> Unit,
    onRemoveCrop: (Int) -> Unit,
    onMoveCrop: (Int, Int, Int) -> Unit,
    onGenerateAi: (List<CropData>, Boolean, Int) -> Unit,
    showAiModalState: Boolean = false,
    onShowAiModalChange: (Boolean) -> Unit = {},
    showAddCropModalState: Boolean = false,
    onShowAddCropModalChange: (Boolean) -> Unit = {},
    onDeleteAll: () -> Unit = {},
    showDeleteAllModalState: Boolean = false,
    onShowDeleteAllModalChange: (Boolean) -> Unit = {}
) {
    var selectedCropForPlacement by remember { mutableStateOf<CropData?>(null) }
    var draggingCropId by remember { mutableStateOf<Int?>(null) }
    var touchStartPos by remember { mutableStateOf<Pair<Double, Double>?>(null) }
    var initialCropPos by remember { mutableStateOf<Pair<Int, Int>?>(null) }
    var longPressTimer by remember { mutableStateOf<Int?>(null) }
    var containerWidth by remember { mutableStateOf(800) }
    var containerHeight by remember { mutableStateOf(600) }
    var showAddCropModal by remember { mutableStateOf(false) }
    var showAiModal by remember { mutableStateOf(false) }
    var showDeleteAllModal by remember { mutableStateOf(false) }
    var contextMenuCropId by remember { mutableStateOf<Int?>(null) }
    var fillingFoilId by remember { mutableStateOf<Int?>(null) }
    var contextMenuPos by remember { mutableStateOf<Pair<Double, Double>?>(null) }
    var showViewCropId by remember { mutableStateOf<Int?>(null) }
    var targetHolePosition by remember { mutableStateOf<Pair<Int, Int>?>(null) }

    // Sync external state with internal state
    LaunchedEffect(showAiModalState) { showAiModal = showAiModalState }
    LaunchedEffect(showAddCropModalState) { showAddCropModal = showAddCropModalState }
    LaunchedEffect(showDeleteAllModalState) { showDeleteAllModal = showDeleteAllModalState }
    LaunchedEffect(showAiModal) { onShowAiModalChange(showAiModal) }
    LaunchedEffect(showAddCropModal) { onShowAddCropModalChange(showAddCropModal) }
    LaunchedEffect(showDeleteAllModal) { onShowDeleteAllModalChange(showDeleteAllModal) }

    // External trigger for delete all modal
    fun triggerDeleteAll() {
        showDeleteAllModal = true
    }

    fun resetContextMenu() {
        contextMenuCropId = null
    }

    fun isInsideAnyFoilHole(
        px: Int,
        py: Int,
        plantedCrops: List<PlantedCrop>,
        currentCrop: PlantedCrop? = null
    ): Boolean {
        if (currentCrop?.foilId != null) return true

        return plantedCrops.any { foil ->
            val foilCrop = foil.crop ?: return@any false
            if (foilCrop.icon != "strawberry_foil") return@any false

            val isHorizontal = foil.positionX == 0 && bed.orientation != "1"
            val actualWidth = if (isHorizontal) bed.width else (foil.customWidth ?: 50)
            val actualHeight = if (isHorizontal) (foil.customHeight ?: 50) else bed.length
            val holeSpacing = foil.holeSpacing ?: foilCrop.spacingBetweenPlants ?: 30
            val rows = foil.rows ?: foilCrop.rows ?: 2

            val yOffsets = if (isHorizontal) {
                if (rows == 1) listOf((actualHeight * 0.5).toInt())
                else {
                    val q = (actualHeight * 0.25).toInt()
                    val tq = (actualHeight * 0.75).toInt()
                    if (tq - q < 20) listOf(
                        (actualHeight * 0.35).toInt(),
                        (actualHeight * 0.65).toInt()
                    )
                    else listOf(q, tq)
                }
            } else emptyList()

            val xOffsets = if (!isHorizontal) {
                if (rows == 1) listOf((actualWidth * 0.5).toInt())
                else {
                    val q = (actualWidth * 0.25).toInt()
                    val tq = (actualWidth * 0.75).toInt()
                    if (tq - q < 20) listOf(
                        (actualWidth * 0.35).toInt(),
                        (actualWidth * 0.65).toInt()
                    )
                    else listOf(q, tq)
                }
            } else emptyList()

            if (isHorizontal) {
                val holeCount = (actualWidth / holeSpacing).coerceAtLeast(1)
                val hPadding = (actualWidth % holeSpacing) / 2
                for (i in 0 until holeCount) {
                    val hX = hPadding + (i * holeSpacing) + (holeSpacing / 2)
                    yOffsets.forEach { hY ->
                        val absX = foil.positionX + hX
                        val absY = foil.positionY + hY
                        if (px >= absX - 2 && px <= absX + 2 && py >= absY - 2 && py <= absY + 2) return@any true
                    }
                }
            } else {
                val holeCount = (actualHeight / holeSpacing).coerceAtLeast(1)
                val vPadding = (actualHeight % holeSpacing) / 2
                for (i in 0 until holeCount) {
                    val hY = vPadding + (i * holeSpacing) + (holeSpacing / 2)
                    xOffsets.forEach { hX ->
                        val absX = foil.positionX + hX
                        val absY = foil.positionY + hY
                        if (px >= absX - 2 && px <= absX + 2 && py >= absY - 2 && py <= absY + 2) return@any true
                    }
                }
            }
            false
        }
    }

    fun copyCrop(planted: PlantedCrop) {
        val crop = planted.crop ?: return
        val plantWidth = (planted.customWidth ?: crop.spacingBetweenRows ?: 20).coerceAtLeast(10)
        val plantHeight =
            (planted.customHeight ?: crop.spacingBetweenPlants ?: 20).coerceAtLeast(10)

        var nextX = planted.positionX + plantWidth
        var nextY = planted.positionY

        if (nextX + plantWidth > bed.width) {
            nextX = planted.positionX
            nextY = planted.positionY + plantHeight
            if (nextY + plantHeight > bed.length) {
                nextX = 0
                nextY = 0
            }
        }
        onAddCrop(crop, nextX, nextY, planted.foilId)
    }

    LaunchedEffect(Unit) {
        val updateDimensions = {
            val container = window.document.getElementById("bed-grid-container")
            // Try to use the narrowest possible measurement to avoid any overflow
            val w = if (container != null) {
                val rect = container.getBoundingClientRect()
                rect.width.toInt().coerceAtMost(window.innerWidth)
            } else window.innerWidth

            // Give enough deduction to ensure left ruler and grid fit properly
            val deduction = 20
            containerWidth = (w - deduction).coerceAtLeast(280)
            containerHeight = (window.innerHeight * 0.75).toInt()
        }
        updateDimensions()
        // Wait for layout and then update again
        window.setTimeout({ updateDimensions() }, 150)
        window.addEventListener("resize") { updateDimensions() }
    }

    Div({
        classes("flex", "flex-col", "gap-6", "max-w-full")
        style {
            property("overflow-x", "visible") // Allow content to be visible
        }
        onClick { contextMenuCropId = null }
    }) {
        // Header
        Div({ classes("flex", "items-center", "justify-between", "mb-4", "gap-2") }) {
            Div({ classes("flex", "items-center", "gap-2", "min-w-0") }) {
                Button({
                    classes(
                        "w-10",
                        "h-10",
                        "rounded-xl",
                        "bg-white",
                        "border",
                        "border-gray-200",
                        "flex-shrink-0",
                        "flex",
                        "items-center",
                        "justify-center",
                        "text-gray-600",
                        "hover:bg-emerald-50"
                    )
                    onClick { onBack() }
                }) { I({ classes("fas", "fa-arrow-left") }) }
                Div({ classes("min-w-0") }) {
                    H2({
                        classes("text-lg", "sm:text-2xl", "font-bold", "text-[#134130]", "truncate")
                    }) { Text(bed.name) }
                    Span({
                        classes(
                            "text-xs",
                            "sm:text-gray-500",
                            "whitespace-nowrap"
                        )
                    }) { Text("${bed.width}x${bed.length}cm") }
                }
            }

            Div({ classes("flex", "gap-1", "sm:gap-3", "flex-shrink-0") }) {
                // Buttons removed - now in main header
            }
        }

        if (isLoading) {
            Div({ classes("text-center", "py-20") }) {
                I({
                    classes(
                        "fas",
                        "fa-circle-notch",
                        "fa-spin",
                        "text-4xl",
                        "text-emerald-500",
                        "mb-4"
                    )
                })
                H2 { Text("Planiranje prostora u toku...") }
            }
        } else {
            // Main Content Row
            Div({ classes("flex", "flex-col", "lg:flex-row", "gap-4", "items-start", "w-full") }) {
                // Main Grid Area
                Div({
                    id("bed-grid-container")
                    classes("flex-1", "relative", "w-full")
                    style {
                        property("overflow-x", "visible") // Allow ruler and grid to be visible
                        property("overflow-y", "visible")
                        property("min-height", "400px")
                    }
                }) {
                    // Inject CSS for rulers and marks
                    org.jetbrains.compose.web.dom.Style {
                        // Ensure marks and text are always sharp
                    }

                    // Available width for the ACTUAL grid (excluding left ruler and right padding)
                    // Left ruler is 50px + 4px spacing = 54px.
                    // We need extra padding on right to ensure full visibility
                    // We need to ensure availableWidth is SMALL enough that scale * bed.width + ruler space + padding <= containerWidth
                    val sideDeduction = 100 // 50px ruler + 4px margin + 46px right padding for safety
                    val availableWidth = (containerWidth - sideDeduction).toDouble().coerceAtLeast(100.0)

                    // The core logic: scale is EXACTLY the ratio that fits the bed width into available width
                    val widthScale = availableWidth / bed.width.toDouble()
                    val scale = widthScale.coerceAtMost(5.0)

                    // Container for rulers and grid
                    Div({
                        classes("flex", "justify-start", "w-full")
                        style {
                            property("margin", "30px 0 20px 0")
                            property("padding-left", "0px") // Remove padding, use ruler width instead
                            property("max-width", "100%")
                            property("box-sizing", "border-box")
                            property("overflow", "visible")
                        }
                    }) {
                        Div({
                            classes("relative", "inline-block")
                            style {
                                property("max-width", "100%")
                            }
                        }) {
                            // Top Ruler (Horizontal)
                            Div({
                                classes("flex", "items-end", "justify-start")
                                style {
                                    width((bed.width * scale).px)
                                    height(30.px)
                                    property("margin-left", "50px") // Align with left ruler
                                    property("position", "relative")
                                    property("overflow", "visible") // Ensure numbers aren't clipped
                                }
                            }) {
                                val horizontalMarks = (0..bed.width step 10).toList()
                                val outlineOffset = 4 // Grid outline width
                                horizontalMarks.forEach { cm ->
                                    Div({
                                        style {
                                            property("position", "absolute")
                                            property("left", "${cm * scale + outlineOffset}px") // Add outline offset
                                            property("bottom", "0")
                                            property("width", "1px")
                                            property(
                                                "height", when {
                                                    cm % 100 == 0 -> "20px"
                                                    cm % 50 == 0 -> "12px"
                                                    else -> "8px"
                                                }
                                            )
                                            property("background-color", "#666")
                                        }
                                    })
                                    if (cm % 100 == 0 || cm == 0) {
                                        Span({
                                            classes("text-xs", "text-gray-600", "font-medium")
                                            style {
                                                property("position", "absolute")
                                                property("left", "${cm * scale + outlineOffset}px") // Add outline offset
                                                property("transform", "translateX(-50%)")
                                                property("top", "0")
                                                property("white-space", "nowrap")
                                            }
                                        }) {
                                            Text("${cm}cm")
                                        }
                                    }
                                }
                            }

                            // Main row with left ruler and grid
                            Div({
                                classes("flex", "items-start")
                                style {
                                    property("overflow", "visible")
                                }
                            }) {
                                // Left Ruler (Vertical)
                                Div({
                                    classes("flex", "flex-col", "items-end", "justify-start")
                                    style {
                                        width(50.px)
                                        height((bed.length * scale).px)
                                        property("margin-right", "4px") // Base margin
                                        property("position", "relative")
                                        property("flex-shrink", "0")
                                    }
                                }) {
                                    val verticalMarks = (0..bed.length step 10).toList()
                                    verticalMarks.forEach { cm ->
                                        Div({
                                            style {
                                                property("position", "absolute")
                                                property("top", "${cm * scale}px") // No offset for vertical
                                                property("right", "0")
                                                property("height", "1px")
                                                property(
                                                    "width", when {
                                                        cm % 100 == 0 -> "20px"
                                                        cm % 50 == 0 -> "12px"
                                                        else -> "8px"
                                                    }
                                                )
                                                property("background-color", "#666")
                                            }
                                        })
                                        if (cm % 100 == 0 || cm == 0) {
                                            Span({
                                                classes("text-xs", "text-gray-600", "font-medium")
                                                style {
                                                    property("position", "absolute")
                                                    property("top", "${cm * scale - 8}px") // No offset, just centering
                                                    property("right", "24px")
                                                    property("white-space", "nowrap")
                                                }
                                            }) {
                                                Text("${cm}cm")
                                            }
                                        }
                                    }
                                }

                                // Bed Grid
                                Div({
                                    id("bed-grid-main")
                                    classes("relative", "select-none")
                                    style {
                                        width((bed.width * scale).px)
                                        height((bed.length * scale).px)
                                        // Using outline instead of border to keep internal elements aligned
                                        property("outline", "4px solid #134130")
                                        property("border-radius", "0px")
                                        property("background", "#F9FAFB")
                                        property("margin-left", "auto")
                                        property("margin-right", "auto")
                                        property("overflow", "visible")
                                        // Prevent scrolling when dragging on mobile
                                        if (draggingCropId != null) {
                                            property("touch-action", "none")
                                        }
                                    }
                                    onClick { event ->
                                        val rect = window.document.getElementById("bed-grid-main")
                                            ?.getBoundingClientRect() ?: return@onClick

                                        // If we click the bed while a crop is selected for placement, add it.
                                        // But if targetHolePosition was set, it means we originated from a hole click,
                                        // and the modal should handle it. The bed click should be ignored if we are in hole mode.
                                        if (targetHolePosition != null) return@onClick

                                        val crop = selectedCropForPlacement ?: return@onClick

                                        val isRotated = bed.orientation == "1"
                                        val isPathway = crop.icon == "pathway"
                                        val isFoil = crop.icon == "strawberry_foil"

                                        // Default sizes for placement
                                        val plantWidth = when {
                                            isPathway -> if (isRotated) (crop.spacingBetweenPlants
                                                ?: 20) else bed.width

                                            isFoil -> if (isRotated) (crop.spacingBetweenPlants
                                                ?: 50) else bed.width

                                            isRotated -> crop.spacingBetweenPlants ?: 20
                                            else -> crop.spacingBetweenRows ?: 20
                                        }.coerceAtLeast(10)

                                        val plantHeight = when {
                                            isPathway -> if (isRotated) bed.length else (crop.spacingBetweenPlants
                                                ?: 20)

                                            isFoil -> if (isRotated) bed.length else 50
                                            isRotated -> crop.spacingBetweenRows ?: 20
                                            else -> crop.spacingBetweenPlants ?: 20
                                        }.coerceAtLeast(10)

                                        var x =
                                            (((event.clientX - rect.left) / scale) - (plantWidth / 2)).toInt()
                                        var y =
                                            (((event.clientY - rect.top) / scale) - (plantHeight / 2)).toInt()

                                        // If it's a full-width horizontal pathway, snap x to 0
                                        if (isPathway && plantWidth == bed.width) {
                                            x = 0
                                        }
                                        // If it's a full-height vertical pathway, snap y to 0
                                        if (isPathway && plantHeight == bed.length) {
                                            y = 0
                                        }
                                        // Foil also snaps
                                        if (isFoil && plantWidth == bed.width) x = 0
                                        if (isFoil && plantHeight == bed.length) y = 0

                                        x = x.coerceIn(0, (bed.width - plantWidth).coerceAtLeast(0))
                                        y = y.coerceIn(
                                            0,
                                            (bed.length - plantHeight).coerceAtLeast(0)
                                        )

                                        onAddCrop(
                                            if (isRotated && (isPathway || isFoil)) crop.copy(
                                                spacingBetweenPlants = plantWidth,
                                                spacingBetweenRows = plantHeight
                                            ) else crop, x, y, null
                                        )
                                        selectedCropForPlacement = null
                                    }
                                    onMouseMove { event ->
                                        val dragId = draggingCropId ?: return@onMouseMove
                                        val rect = window.document.getElementById("bed-grid-main")
                                            ?.getBoundingClientRect() ?: return@onMouseMove

                                        val plantedMoved =
                                            plantedCrops.find { it.id == dragId }
                                                ?: return@onMouseMove
                                        val crop = plantedMoved.crop ?: return@onMouseMove
                                        val isRotated = bed.orientation == "1"
                                        val isPathway =
                                            crop.icon == "pathway" || crop.name.lowercase()
                                                .contains("staza")
                                        val isFoil = crop.icon == "strawberry_foil"

                                        // Logic based on User Request:
                                        // positionX == 0 -> Horizontal pathway, full width, height = customHeight
                                        // positionX != 0 -> Vertical pathway, full height, width = customHeight, offset by positionX
                                        val isHorizontalPathway =
                                            isPathway && (plantedMoved.positionX == 0 && !isRotated)
                                        val isVerticalPathway =
                                            isPathway && (plantedMoved.positionX != 0 || isRotated)

                                        val isHorizontalFoil =
                                            isFoil && (plantedMoved.positionX == 0 && !isRotated)
                                        val isVerticalFoil =
                                            isFoil && (plantedMoved.positionX != 0 || isRotated)

                                        val plantWidthMoved = when {
                                            isHorizontalPathway -> bed.width
                                            isHorizontalFoil -> bed.width
                                            isVerticalPathway -> plantedMoved.customWidth ?: 20
                                            isVerticalFoil -> plantedMoved.customWidth ?: 50
                                            plantedMoved.customWidth != null -> plantedMoved.customWidth
                                            isRotated -> crop.spacingBetweenPlants ?: 20
                                            else -> crop.spacingBetweenRows ?: 20
                                        }.coerceAtLeast(10)

                                        val plantHeightMoved = when {
                                            isHorizontalPathway -> plantedMoved.customHeight ?: 20
                                            isHorizontalFoil -> plantedMoved.customHeight ?: 50
                                            isVerticalPathway -> bed.length
                                            isVerticalFoil -> bed.length
                                            plantedMoved.customHeight != null -> plantedMoved.customHeight
                                            isRotated -> crop.spacingBetweenRows ?: 20
                                            else -> crop.spacingBetweenPlants ?: 20
                                        }.coerceAtLeast(10)

                                        var x =
                                            (((event.clientX - rect.left) / scale) - (plantWidthMoved / 2)).toInt()
                                        var y =
                                            (((event.clientY - rect.top) / scale) - (plantHeightMoved / 2)).toInt()

                                        if (isHorizontalPathway) x = 0
                                        if (isVerticalPathway) y = 0

                                        x = x.coerceIn(
                                            0,
                                            (bed.width - plantWidthMoved).coerceAtLeast(0)
                                        )
                                        y = y.coerceIn(
                                            0,
                                            (bed.length - plantHeightMoved).coerceAtLeast(0)
                                        )

                                        val existing = plantedCrops.find { it.id == dragId }
                                        if (existing != null && (existing.positionX != x || existing.positionY != y)) {
                                            val dx = x - existing.positionX
                                            val dy = y - existing.positionY

                                            // Move the dragged item itself
                                            onMoveCrop(dragId, x, y)

                                            // If it's a foil, move all its crops
                                            if (crop.icon == "strawberry_foil") {
                                                plantedCrops.filter { it.foilId == draggingCropId }
                                                    .forEach { child ->
                                                        onMoveCrop(
                                                            child.id,
                                                            child.positionX + dx,
                                                            child.positionY + dy
                                                        )
                                                    }
                                            }
                                        }
                                    }
                                    onMouseUp { draggingCropId = null }
                                    onMouseLeave { draggingCropId = null }
                                }) {
                                    // Removed AI Zone Visualization as per request

                                    // Grid lines - every 10cm with light lines, every 50cm with stronger lines
                                    for (i in 0..(bed.width / 10)) {
                                        val xPos = i * 10 * scale
                                        if (xPos <= bed.width * scale) {
                                            val isMainGrid = (i * 10) % 50 == 0 // Every 50cm
                                            Div({
                                                classes(
                                                    "absolute",
                                                    "h-full",
                                                    "w-[1px]",
                                                    if (isMainGrid) "bg-gray-400/50" else "bg-gray-300/30"
                                                )
                                                style { left(xPos.px) }
                                            })
                                        }
                                    }
                                    for (i in 0..(bed.length / 10)) {
                                        val yPos = i * 10 * scale
                                        if (yPos <= bed.length * scale) {
                                            val isMainGrid = (i * 10) % 50 == 0 // Every 50cm
                                            Div({
                                                classes(
                                                    "absolute",
                                                    "w-full",
                                                    "h-[1px]",
                                                    if (isMainGrid) "bg-gray-400/50" else "bg-gray-300/30"
                                                )
                                                style { top(yPos.px) }
                                            })
                                        }
                                    }

                                    // Planted Crops
                                    plantedCrops.forEach { planted ->
                                        val crop = planted.crop ?: return@forEach
                                        val isRotatedCurrent = bed.orientation == "1"
                                        val isPathwayCurrent =
                                            crop.icon == "pathway" || crop.name.lowercase()
                                                .contains("staza")
                                        val isFoilCurrent = crop.icon == "strawberry_foil"

                                        // Determine if a crop is "inside" a foil hole
                                        // We check if its coordinates match any hole on any foil
                                        val isInsideFoilHoleLocal = isInsideAnyFoilHole(
                                            planted.positionX,
                                            planted.positionY,
                                            plantedCrops,
                                            planted
                                        )

                                        val isInsideAnyFoil = plantedCrops.any { other ->
                                            other.crop?.icon == "strawberry_foil" &&
                                                    planted.positionX >= other.positionX &&
                                                    planted.positionX < other.positionX + (other.customWidth
                                                ?: 50) &&
                                                    planted.positionY >= other.positionY &&
                                                    planted.positionY < other.positionY + (other.customHeight
                                                ?: 50)
                                        }

                                        // Logic based on User Request:
                                        // positionX == 0 -> Horizontal pathway, full width, height = customHeight
                                        // positionX != 0 -> Vertical pathway, full height, width = customHeight, offset by positionX
                                        val isHorizontalPathwayCurrent =
                                            isPathwayCurrent && (planted.positionX == 0 && !isRotatedCurrent)
                                        val isVerticalPathwayCurrent =
                                            isPathwayCurrent && (planted.positionX != 0 || isRotatedCurrent)

                                        val isHorizontalFoilCurrent =
                                            isFoilCurrent && (planted.positionX == 0 && !isRotatedCurrent)
                                        val isVerticalFoilCurrent =
                                            isFoilCurrent && (planted.positionX != 0 || isRotatedCurrent)

                                        val plantWidthCurrent = when {
                                            isHorizontalPathwayCurrent -> bed.width
                                            isHorizontalFoilCurrent -> bed.width
                                            isVerticalPathwayCurrent -> planted.customWidth ?: 20
                                            isVerticalFoilCurrent -> planted.customWidth ?: 50
                                            planted.customWidth != null -> planted.customWidth
                                            isRotatedCurrent -> crop.spacingBetweenPlants ?: 20
                                            else -> crop.spacingBetweenRows ?: 20
                                        }.coerceAtLeast(10)

                                        val plantHeightCurrent = when {
                                            isHorizontalPathwayCurrent -> planted.customHeight ?: 20
                                            isHorizontalFoilCurrent -> planted.customHeight ?: 50
                                            isVerticalPathwayCurrent -> bed.length
                                            isVerticalFoilCurrent -> bed.length
                                            planted.customHeight != null -> planted.customHeight
                                            isRotatedCurrent -> crop.spacingBetweenRows ?: 20
                                            else -> crop.spacingBetweenPlants ?: 20
                                        }.coerceAtLeast(10)

                                        if (isInsideFoilHoleLocal && !isFoilCurrent) {
                                            // Don't render these here, they are rendered inside the foil
                                            return@forEach
                                        }

                                        Div({
                                            classes(
                                                "absolute",
                                                "flex",
                                                "items-center",
                                                "justify-center",
                                                "group",
                                                "cursor-move"
                                            )
                                            style {
                                                left((planted.positionX * scale).px)
                                                top((planted.positionY * scale).px)
                                                width((plantWidthCurrent * scale).px)
                                                height((plantHeightCurrent * scale).px)
                                                if (draggingCropId == planted.id) property(
                                                    "z-index",
                                                    "100"
                                                ) else property("z-index", "10")
                                                // Disable default touch behaviors to allow dragging
                                                property("touch-action", "none")
                                            }
                                            onMouseDown { event ->
                                                event.stopPropagation()
                                                if (event.button.toInt() == 0) { // Left click
                                                    draggingCropId = planted.id
                                                    contextMenuCropId = null
                                                }
                                            }
                                            onContextMenu { event ->
                                                event.preventDefault()
                                                event.stopPropagation()
                                                contextMenuCropId = planted.id
                                                contextMenuPos = event.pageX to event.pageY
                                            }
                                            // Mobile Touch Events for Dragging
                                            onTouchStart { event ->
                                                // Stop propagation to prevent bed-level clicks
                                                event.stopPropagation()

                                                val dynEvent = event.asDynamic()
                                                val touch =
                                                    if (dynEvent.touches != null && dynEvent.touches.length > 0) {
                                                        dynEvent.touches[0]
                                                    } else null

                                                if (touch != null) {
                                                    val startX = touch.clientX.unsafeCast<Double>()
                                                    val startY = touch.clientY.unsafeCast<Double>()
                                                    touchStartPos = startX to startY
                                                    initialCropPos =
                                                        planted.positionX to planted.positionY

                                                    // Clear any existing timer
                                                    longPressTimer?.let { window.clearTimeout(it) }

                                                    // Set a timer for 400ms to trigger dragging
                                                    longPressTimer = window.setTimeout({
                                                        draggingCropId = planted.id
                                                        contextMenuCropId = null
                                                        // Vibrate if available
                                                        window.asDynamic().navigator?.vibrate(50)
                                                    }, 400)
                                                }
                                            }
                                            onTouchMove { event ->
                                                val dynEvent = event.asDynamic()
                                                val touch =
                                                    if (dynEvent.touches != null && dynEvent.touches.length > 0) {
                                                        dynEvent.touches[0]
                                                    } else null

                                                if (draggingCropId == planted.id) {
                                                    // Once we are officially dragging, we MUST prevent scroll
                                                    event.preventDefault()
                                                    event.stopPropagation()

                                                    val startPos =
                                                        touchStartPos ?: return@onTouchMove
                                                    val initPos =
                                                        initialCropPos ?: return@onTouchMove

                                                    if (touch == null) return@onTouchMove

                                                    val currentTouchX =
                                                        touch.clientX.unsafeCast<Double>()
                                                    val currentTouchY =
                                                        touch.clientY.unsafeCast<Double>()

                                                    val dx =
                                                        ((currentTouchX - startPos.first) / scale).toInt()
                                                    val dy =
                                                        ((currentTouchY - startPos.second) / scale).toInt()

                                                    var x = initPos.first + dx
                                                    var y = initPos.second + dy

                                                    val latestPlanted =
                                                        plantedCrops.find { it.id == planted.id }
                                                            ?: return@onTouchMove
                                                    val cropMoved =
                                                        latestPlanted.crop ?: return@onTouchMove
                                                    val isRotated = bed.orientation == "1"
                                                    val isPathway =
                                                        cropMoved.icon == "pathway" || cropMoved.name.lowercase()
                                                            .contains("staza")
                                                    val isFoil = cropMoved.icon == "strawberry_foil"

                                                    val plantWidthMoved = when {
                                                        isPathway && (latestPlanted.positionX == 0 && !isRotated) -> bed.width
                                                        isFoil && (latestPlanted.positionX == 0 && !isRotated) -> bed.width
                                                        isPathway && (latestPlanted.positionX != 0 || isRotated) -> latestPlanted.customWidth
                                                            ?: 20

                                                        isFoil && (latestPlanted.positionX != 0 || isRotated) -> latestPlanted.customWidth
                                                            ?: 50

                                                        latestPlanted.customWidth != null -> latestPlanted.customWidth
                                                        isRotated -> cropMoved.spacingBetweenPlants
                                                            ?: 20

                                                        else -> cropMoved.spacingBetweenRows ?: 20
                                                    }.coerceAtLeast(10)

                                                    val plantHeightMoved = when {
                                                        isPathway && (latestPlanted.positionX == 0 && !isRotated) -> latestPlanted.customHeight
                                                            ?: 20

                                                        isFoil && (latestPlanted.positionX == 0 && !isRotated) -> latestPlanted.customHeight
                                                            ?: 50

                                                        isPathway && (latestPlanted.positionX != 0 || isRotated) -> bed.length
                                                        isFoil && (latestPlanted.positionX != 0 || isRotated) -> bed.length
                                                        latestPlanted.customHeight != null -> latestPlanted.customHeight
                                                        isRotated -> cropMoved.spacingBetweenRows
                                                            ?: 20

                                                        else -> cropMoved.spacingBetweenPlants ?: 20
                                                    }.coerceAtLeast(10)

                                                    if (isPathway && (latestPlanted.positionX == 0 && !isRotated)) x =
                                                        0
                                                    if (isPathway && (latestPlanted.positionX != 0 || isRotated)) y =
                                                        0

                                                    x = x.coerceIn(
                                                        0,
                                                        (bed.width - plantWidthMoved).coerceAtLeast(0)
                                                    )
                                                    y = y.coerceIn(
                                                        0,
                                                        (bed.length - plantHeightMoved).coerceAtLeast(0)
                                                    )

                                                    if (latestPlanted.positionX != x || latestPlanted.positionY != y) {
                                                        val diffX = x - latestPlanted.positionX
                                                        val diffY = y - latestPlanted.positionY

                                                        // Move the item
                                                        onMoveCrop(planted.id, x, y)

                                                        // Move child crops if foil
                                                        if (cropMoved.icon == "strawberry_foil") {
                                                            plantedCrops.filter { it.foilId == planted.id }
                                                                .forEach { child ->
                                                                    onMoveCrop(
                                                                        child.id,
                                                                        child.positionX + diffX,
                                                                        child.positionY + diffY
                                                                    )
                                                                }
                                                        }
                                                    }
                                                } else {
                                                    // If we haven't started dragging but the finger moved significantly,
                                                    // cancel the long-press timer so the browser can scroll.
                                                    val startPos = touchStartPos
                                                    if (startPos != null && touch != null) {
                                                        val currentX =
                                                            touch.clientX.unsafeCast<Double>()
                                                        val currentY =
                                                            touch.clientY.unsafeCast<Double>()
                                                        val moveX =
                                                            if (currentX > startPos.first) currentX - startPos.first else startPos.first - currentX
                                                        val moveY =
                                                            if (currentY > startPos.second) currentY - startPos.second else startPos.second - currentY
                                                        if (moveX > 15 || moveY > 15) {
                                                            longPressTimer?.let {
                                                                window.clearTimeout(
                                                                    it
                                                                )
                                                            }
                                                            longPressTimer = null
                                                        }
                                                    }
                                                }
                                            }
                                            onTouchEnd { event ->
                                                longPressTimer?.let { window.clearTimeout(it) }
                                                longPressTimer = null

                                                if (draggingCropId == null && touchStartPos != null) {
                                                    // Detection of a quick tap (click) vs long-press hold
                                                    val dynEvent = event.asDynamic()
                                                    val touchPoint =
                                                        if (dynEvent.changedTouches != null && dynEvent.changedTouches.length > 0) {
                                                            dynEvent.changedTouches[0]
                                                        } else null

                                                    var isTap = true
                                                    if (touchPoint != null) {
                                                        val dx =
                                                            kotlin.math.abs(touchPoint.clientX.unsafeCast<Double>() - touchStartPos!!.first)
                                                        val dy =
                                                            kotlin.math.abs(touchPoint.clientY.unsafeCast<Double>() - touchStartPos!!.second)
                                                        if (dx > 10 || dy > 10) isTap = false
                                                    }

                                                    if (isTap) {
                                                        // Reveal context menu on quick tap
                                                        contextMenuCropId = planted.id
                                                        val startPos = touchStartPos!!
                                                        contextMenuPos =
                                                            startPos.first to startPos.second
                                                    }
                                                }

                                                draggingCropId = null
                                                touchStartPos = null
                                                initialCropPos = null
                                            }
                                            onTouchCancel {
                                                longPressTimer?.let { window.clearTimeout(it) }
                                                longPressTimer = null
                                                draggingCropId = null
                                                touchStartPos = null
                                                initialCropPos = null
                                            }
                                        }) {
                                            if (isPathwayCurrent) {
                                                Div({
                                                    classes(
                                                        "absolute",
                                                        "inset-0",
                                                        "bg-[#8B4513]/40",
                                                        "border",
                                                        "border-[#5D2E0C]",
                                                        "rounded-none", // Removed rounding on pathway
                                                        "overflow-hidden",
                                                        "shadow-sm"
                                                    )
                                                }) {
                                                    if (isHorizontalPathwayCurrent) {
                                                        for (i in 1 until (plantHeightCurrent / 5)) {
                                                            Div({
                                                                classes(
                                                                    "absolute",
                                                                    "w-full",
                                                                    "h-[1px]",
                                                                    "bg-[#5D2E0C]/30"
                                                                ); style { top((i * 5 * scale).px) }
                                                            })
                                                        }
                                                    } else {
                                                        // Vertical lines for vertical pathway
                                                        for (i in 1 until (plantWidthCurrent / 5)) {
                                                            Div({
                                                                classes(
                                                                    "absolute",
                                                                    "h-full",
                                                                    "w-[1px]",
                                                                    "bg-[#5D2E0C]/30"
                                                                ); style { left((i * 5 * scale).px) }
                                                            })
                                                        }
                                                    }
                                                }
                                            } else if (isFoilCurrent) {
                                                Div({
                                                    classes(
                                                        "absolute",
                                                        "inset-0",
                                                        "bg-gray-900",
                                                        "border",
                                                        "border-black",
                                                        "rounded-none", // Removed rounding on foil
                                                        "overflow-hidden",
                                                        "shadow-sm"
                                                    )
                                                }) {
                                                    val isHorizontal = isHorizontalFoilCurrent
                                                    val actualWidth =
                                                        if (isHorizontal) bed.width else (planted.customWidth
                                                            ?: 50)
                                                    val actualHeight =
                                                        if (isHorizontal) (planted.customHeight
                                                            ?: 50) else bed.length

                                                    val cropInFoil = planted.crop
                                                    val holeSpacing = planted.holeSpacing
                                                        ?: cropInFoil.spacingBetweenPlants ?: 30
                                                    val rows = planted.rows ?: cropInFoil.rows ?: 2

                                                    Div({
                                                        style {
                                                            position(Position.Relative)
                                                            width(100.percent)
                                                            height(100.percent)
                                                        }
                                                    }) {
                                                        if (isHorizontal) {
                                                            val holeCount =
                                                                (actualWidth / holeSpacing).coerceAtLeast(
                                                                    1
                                                                )
                                                            val horizontalPadding =
                                                                (actualWidth % holeSpacing) / 2

                                                            val yOffsets = if (rows == 1) {
                                                                listOf((actualHeight * 0.5).toInt())
                                                            } else {
                                                                val quarter =
                                                                    (actualHeight * 0.25).toInt()
                                                                val threeQuarters =
                                                                    (actualHeight * 0.75).toInt()
                                                                // Ensure holes don't overlap vertically if foil is narrow
                                                                if (threeQuarters - quarter < 20) {
                                                                    listOf(
                                                                        (actualHeight * 0.35).toInt(),
                                                                        (actualHeight * 0.65).toInt()
                                                                    )
                                                                } else {
                                                                    listOf(quarter, threeQuarters)
                                                                }
                                                            }

                                                            repeat(holeCount) { index ->
                                                                val centerX =
                                                                    horizontalPadding + (index * holeSpacing) + (holeSpacing / 2)

                                                                yOffsets.forEach { yOffset ->
                                                                    // Calculate adaptive hole size based on scale and spacing
                                                                    // We want the hole to be roughly a fraction of the available spacing but also proportional to scale
                                                                    val baseSizeRem =
                                                                        1.8 // Increased from 1.4
                                                                    val adaptiveScale =
                                                                        (scale / 2.0).coerceIn(
                                                                            0.6,
                                                                            1.4
                                                                        ) // Increased from 0.5, 1.2
                                                                    // If spacing is very tight, shrink holes further to prevent collisions
                                                                    // Using a more generous spacing factor to allow holes to grow more
                                                                    val spacingFactor =
                                                                        if (holeSpacing < 40) (holeSpacing / 25.0).coerceAtLeast(
                                                                            1.0
                                                                        ) else 1.25
                                                                    val finalHoleSize =
                                                                        (baseSizeRem * adaptiveScale * spacingFactor).cssRem

                                                                    Div({
                                                                        classes(
                                                                            "bg-[#F9FAFB]",
                                                                            "rounded-full",
                                                                            "border",
                                                                            "border-gray-800",
                                                                            "shadow-inner",
                                                                            "cursor-pointer",
                                                                            "hover:bg-blue-100",
                                                                            "absolute",
                                                                            "flex",
                                                                            "items-center",
                                                                            "justify-center",
                                                                            "overflow-hidden"
                                                                        )
                                                                        style {
                                                                            width(finalHoleSize)
                                                                            height(finalHoleSize)
                                                                            // Use max-width/max-height to ensure they don't exceed available space with some margin
                                                                            val maxAllowedPx =
                                                                                (holeSpacing * 0.85 * scale).px
                                                                            property(
                                                                                "max-width",
                                                                                maxAllowedPx
                                                                            )
                                                                            property(
                                                                                "max-height",
                                                                                maxAllowedPx
                                                                            )
                                                                            left((centerX * scale).px)
                                                                            top((yOffset * scale).px)
                                                                            property(
                                                                                "transform",
                                                                                "translate(-50%, -50%)"
                                                                            )
                                                                        }
                                                                        onClick { event ->
                                                                            event.preventDefault(); event.stopPropagation(); targetHolePosition =
                                                                            (planted.positionX + centerX) to (planted.positionY + yOffset); showAddCropModal =
                                                                            true
                                                                        }
                                                                    }) {
                                                                        val holeX =
                                                                            planted.positionX + centerX
                                                                        val holeY =
                                                                            planted.positionY + yOffset
                                                                        val cropInHole =
                                                                            plantedCrops.find {
                                                                                (it.foilId == planted.id || (it.positionX >= holeX - 2 && it.positionX <= holeX + 2 && it.positionY >= holeY - 2 && it.positionY <= holeY + 2)) && it.crop?.icon != "strawberry_foil"
                                                                            }
                                                                        cropInHole?.crop?.let { c ->
                                                                            getCropImageSource(
                                                                                c.icon
                                                                            )?.let {
                                                                                Img(
                                                                                    src = it,
                                                                                    attrs = {
                                                                                        classes(
                                                                                            "w-full",
                                                                                            "h-full",
                                                                                            "object-contain"
                                                                                        )
                                                                                    })
                                                                            }
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        } else {
                                                            // Vertical Layout for non-X=0 foils
                                                            val holeCount =
                                                                (actualHeight / holeSpacing).coerceAtLeast(
                                                                    1
                                                                )
                                                            val verticalPadding =
                                                                (actualHeight % holeSpacing) / 2

                                                            val xOffsets = if (rows == 1) {
                                                                listOf((actualWidth * 0.5).toInt())
                                                            } else {
                                                                val quarter =
                                                                    (actualWidth * 0.25).toInt()
                                                                val threeQuarters =
                                                                    (actualWidth * 0.75).toInt()
                                                                // Ensure holes don't overlap horizontally if foil is narrow
                                                                if (threeQuarters - quarter < 20) {
                                                                    listOf(
                                                                        (actualWidth * 0.35).toInt(),
                                                                        (actualWidth * 0.65).toInt()
                                                                    )
                                                                } else {
                                                                    listOf(quarter, threeQuarters)
                                                                }
                                                            }

                                                            repeat(holeCount) { index ->
                                                                val centerY =
                                                                    verticalPadding + (index * holeSpacing) + (holeSpacing / 2)

                                                                xOffsets.forEach { xOffset ->
                                                                    // Calculate adaptive hole size based on scale and spacing
                                                                    val baseSizeRem =
                                                                        1.8 // Increased from 1.4
                                                                    val adaptiveScale =
                                                                        (scale / 2.0).coerceIn(
                                                                            0.6,
                                                                            1.4
                                                                        ) // Increased from 0.5, 1.2
                                                                    val spacingFactor =
                                                                        if (holeSpacing < 40) (holeSpacing / 25.0).coerceAtLeast(
                                                                            1.0
                                                                        ) else 1.25
                                                                    val finalHoleSize =
                                                                        (baseSizeRem * adaptiveScale * spacingFactor).cssRem

                                                                    Div({
                                                                        classes(
                                                                            "bg-[#F9FAFB]",
                                                                            "rounded-full",
                                                                            "border",
                                                                            "border-gray-800",
                                                                            "shadow-inner",
                                                                            "cursor-pointer",
                                                                            "hover:bg-blue-100",
                                                                            "absolute",
                                                                            "flex",
                                                                            "items-center",
                                                                            "justify-center",
                                                                            "overflow-hidden"
                                                                        )
                                                                        style {
                                                                            width(finalHoleSize)
                                                                            height(finalHoleSize)
                                                                            // Use max-width/max-height to ensure they don't exceed available space with some margin
                                                                            val maxAllowedPx =
                                                                                (holeSpacing * 0.85 * scale).px
                                                                            property(
                                                                                "max-width",
                                                                                maxAllowedPx
                                                                            )
                                                                            property(
                                                                                "max-height",
                                                                                maxAllowedPx
                                                                            )
                                                                            top((centerY * scale).px)
                                                                            left((xOffset * scale).px)
                                                                            property(
                                                                                "transform",
                                                                                "translate(-50%, -50%)"
                                                                            )
                                                                        }
                                                                        onClick { event ->
                                                                            event.preventDefault(); event.stopPropagation(); targetHolePosition =
                                                                            (planted.positionX + xOffset) to (planted.positionY + centerY); showAddCropModal =
                                                                            true
                                                                        }
                                                                    }) {
                                                                        val hX =
                                                                            planted.positionX + xOffset
                                                                        val hY =
                                                                            planted.positionY + centerY
                                                                        val ciH =
                                                                            plantedCrops.find {
                                                                                (it.foilId == planted.id || (it.positionX >= hX - 2 && it.positionX <= hX + 2 && it.positionY >= hY - 2 && it.positionY <= hY + 2)) && it.crop?.icon != "strawberry_foil"
                                                                            }
                                                                        ciH?.crop?.let { c ->
                                                                            getCropImageSource(
                                                                                c.icon
                                                                            )?.let {
                                                                                Img(
                                                                                    src = it,
                                                                                    attrs = {
                                                                                        classes(
                                                                                            "w-full",
                                                                                            "h-full",
                                                                                            "object-contain"
                                                                                        )
                                                                                    })
                                                                            }
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            } else if (crop.isCompanion) {
                                                Div({
                                                    classes(
                                                        "absolute",
                                                        "inset-0",
                                                        "bg-white",
                                                        "border-2",
                                                        "border-purple-500",
                                                        "rounded-lg",
                                                        "shadow-sm"
                                                    )
                                                })
                                            } else {
                                                Div({
                                                    classes(
                                                        "absolute",
                                                        "inset-0",
                                                        "bg-emerald-500/20",
                                                        "border-2",
                                                        "border-emerald-600",
                                                        "rounded-lg",
                                                        "shadow-sm"
                                                    )
                                                })
                                            }

                                            val imageSource = getCropImageSource(crop.icon)
                                            if (isFoilCurrent) {
                                                // No icon for foil
                                            } else if (imageSource != null && !crop.name.lowercase()
                                                    .contains("staza")
                                            ) {
                                                val iconSize = if (isInsideAnyFoil) listOf(
                                                    "w-6",
                                                    "h-6"
                                                ) else listOf("w-10", "h-10")
                                                Img(src = imageSource, attrs = {
                                                    classes(
                                                        *iconSize.toTypedArray(),
                                                        "object-contain",
                                                        "pointer-events-none"
                                                    )
                                                })
                                            } else if (!crop.name.lowercase().contains("staza")) {
                                                I({
                                                    classes(
                                                        "fas",
                                                        "fa-leaf",
                                                        "text-emerald-600",
                                                        "text-lg",
                                                        "relative",
                                                        "z-10"
                                                    )
                                                    style { property("pointer-events", "none") }
                                                })
                                            }

                                            // Removed inline buttons as they are now in context menu
                                        }
                                    }
                                }


                                if (selectedCropForPlacement != null) {
                                    Div({
                                        classes(
                                            "absolute",
                                            "bottom-8",
                                            "left-1/2",
                                            "-translate-x-1/2",
                                            "bg-emerald-600",
                                            "text-white",
                                            "px-6",
                                            "py-3",
                                            "rounded-full",
                                            "shadow-xl",
                                            "z-30",
                                            "flex",
                                            "items-center",
                                            "gap-3"
                                        )
                                    }) {
                                        Text("Izaberite mjesto za ${selectedCropForPlacement?.name ?: ""}")
                                        Button({
                                            classes(
                                                "bg-black/20",
                                                "hover:bg-black/40",
                                                "rounded-full",
                                                "w-6",
                                                "h-6",
                                                "flex",
                                                "items-center",
                                                "justify-center"
                                            ); onClick { selectedCropForPlacement = null }
                                        }) { I({ classes("fas", "fa-times") }) }
                                    }
                                }
                            } // End flex row (left ruler + grid)
                        } // End inline-block container
                    } // End centering wrapper

                    // Inventory Section (Posađene kulture) - moved below grid
                    Div({
                        classes("mt-8", "w-fit", "px-4", "sm:px-0")
                    }) {
                    Div({
                        classes(
                            "bg-white",
                            "rounded-2xl",
                            "p-6",
                            "border",
                            "border-gray-100",
                            "shadow-sm"
                        )
                    }) {
                        H3({
                            classes(
                                "font-bold",
                                "text-[#134130]",
                                "mb-4"
                            )
                        }) { Text("Posađene kulture:") }

                        // Group crops by ID and count them
                        val cropCounts =
                            plantedCrops.filter { it.crop?.icon != "strawberry_foil" }
                                .filter { it.foilId == null }
                                .groupBy { it.crop?.id }
                                .mapValues { it.value.size }
                                .entries
                                .sortedByDescending { it.value }

                        val foils = plantedCrops.filter { it.crop?.icon == "strawberry_foil" }
                            .map {
                                val children = plantedCrops.filter { it1 -> it1.foilId == it.id }
                                val firstChild = children.firstOrNull()
                                Triple(
                                    it,
                                    firstChild,
                                    it.getHolesCount(bed)
                                )
                            }

                        if (cropCounts.isEmpty() && foils.isEmpty()) {
                            P({
                                classes(
                                    "text-gray-400",
                                    "italic",
                                    "text-sm"
                                )
                            }) { Text("Leja je još uvijek prazna.") }
                        } else {
                            Div({ classes("space-y-3") }) {
                                cropCounts.forEach { (cropId, count) ->
                                    val crop = plantedCrops.first { it.crop?.id == cropId }.crop
                                    if (crop != null) {
                                        Div({
                                            classes(
                                                "flex",
                                                "items-center",
                                                "justify-between",
                                                "gap-4"
                                            )
                                        }) {
                                            Div({ classes("flex", "items-center", "gap-2") }) {
                                                Div({
                                                    classes(
                                                        "w-2",
                                                        "h-2",
                                                        "rounded-full",
                                                        "bg-emerald-500"
                                                    )
                                                })
                                                Span({
                                                    classes(
                                                        "text-sm",
                                                        "font-medium",
                                                        "text-gray-700"
                                                    )
                                                }) { Text(crop.name) }
                                            }
                                            Div({ classes("flex", "items-center", "gap-3") }) {
                                                Span({
                                                    classes(
                                                        "text-sm",
                                                        "font-black",
                                                        "text-emerald-700"
                                                    )
                                                }) { Text("x$count") }
                                                I({
                                                    classes(
                                                        "fas",
                                                        "fa-times",
                                                        "text-red-300",
                                                        "text-xs",
                                                        "cursor-pointer",
                                                        "hover:text-red-500"
                                                    )
                                                    onClick {
                                                        val toDelete =
                                                            plantedCrops.find { it.crop?.id == cropId }
                                                        if (toDelete != null) onRemoveCrop(toDelete.id)
                                                    }
                                                })
                                            }
                                        }
                                    }
                                }

                                foils.filter { it.second != null }.forEach { foilTriple ->
                                    val foilId = foilTriple.first.id
                                    Div({
                                        classes(
                                            "flex",
                                            "items-center",
                                            "justify-between",
                                            "gap-4"
                                        )
                                    }) {
                                        Div({ classes("flex", "items-center", "gap-2") }) {
                                            Div({
                                                classes(
                                                    "w-2",
                                                    "h-2",
                                                    "rounded-full",
                                                    "bg-emerald-500"
                                                )
                                            })
                                            Span({
                                                classes(
                                                    "text-sm",
                                                    "font-medium",
                                                    "text-gray-700"
                                                )
                                            }) { Text("Folija sa ${foilTriple.second?.crop?.name}") }
                                        }
                                        Div({ classes("flex", "items-center", "gap-3") }) {
                                            Span({
                                                classes(
                                                    "text-sm",
                                                    "font-black",
                                                    "text-emerald-700"
                                                )
                                            }) { Text("x${foilTriple.third}") }
                                            I({
                                                classes(
                                                    "fas",
                                                    "fa-times",
                                                    "text-red-300",
                                                    "text-xs",
                                                    "cursor-pointer",
                                                    "hover:text-red-500"
                                                )
                                                onClick {
                                                    onRemoveCrop(foilId)
                                                }
                                            })
                                        }
                                    }
                                }
                            }
                        }
                    }
                    } // End Inventory Section

                    // AI Suggestions (after inventory, still inside bed-grid-container)
                    aiSuggestions?.let {
                        Div({
                            classes("mt-8", "w-full", "px-4", "sm:px-0")
                        }) {
                            AiSuggestionsView(suggestions = it)
                        }
                    }
                } // End bed-grid-container
            }
        }
    }

// Context Menu
    contextMenuCropId?.let { cropId ->
        val pos = contextMenuPos ?: return@let
        val planted = plantedCrops.find { it.id == cropId } ?: return@let
        val isFoil = planted.crop?.icon == "strawberry_foil"

        Div({
            classes(
                "fixed",
                "bg-white",
                "rounded-xl",
                "shadow-xl",
                "border",
                "border-gray-100",
                "py-2",
                "z-[200]",
                "min-w-[160px]"
            )
            style {
                left(pos.first.px)
                top(pos.second.px)
            }
            onClick { it.stopPropagation() }
        }) {
            if (isFoil) {
                // Fill Foil Option
                Div({
                    classes(
                        "px-4",
                        "py-2",
                        "hover:bg-emerald-50",
                        "cursor-pointer",
                        "flex",
                        "items-center",
                        "gap-3",
                        "text-emerald-700",
                        "transition-colors",
                        "border-b",
                        "border-gray-50"
                    )
                    onClick {
                        fillingFoilId = cropId
                        showAddCropModal = true
                        resetContextMenu()
                    }
                }) {
                    I({ classes("fas", "fa-fill-drip", "w-5") })
                    Span({ classes("font-medium") }) { Text("Popuni cijelu foliju") }
                }
            }

            // Copy Option
            Div({
                classes(
                    "px-4",
                    "py-2",
                    "hover:bg-emerald-50",
                    "cursor-pointer",
                    "flex",
                    "items-center",
                    "gap-3",
                    "text-gray-700",
                    "transition-colors"
                )
                onClick {
                    copyCrop(planted)
                    resetContextMenu()
                }
            }) {
                I({ classes("fas", "fa-copy", "text-blue-500", "w-5") })
                Span({ classes("font-medium") }) { Text("Dupliciraj") }
            }

            // Delete Option
            Div({
                classes(
                    "px-4",
                    "py-2",
                    "hover:bg-red-50",
                    "cursor-pointer",
                    "flex",
                    "items-center",
                    "gap-3",
                    "text-red-600",
                    "transition-colors"
                )
                onClick {
                    onRemoveCrop(cropId)
                    resetContextMenu()
                }
            }) {
                I({ classes("fas", "fa-trash", "w-5") })
                Span({ classes("font-medium") }) { Text("Obriši") }
            }

            // View Option
            Div({
                classes(
                    "px-4",
                    "py-2",
                    "hover:bg-blue-50",
                    "cursor-pointer",
                    "flex",
                    "items-center",
                    "gap-3",
                    "text-blue-600",
                    "transition-colors",
                    "border-t",
                    "border-gray-50"
                )
                onClick {
                    showViewCropId = cropId
                    resetContextMenu()
                }
            }) {
                I({ classes("fas", "fa-eye", "w-5") })
                Span({ classes("font-medium") }) { Text("Pregledaj") }
            }
        }
    }

// View Crop Modal
    showViewCropId?.let { cropId ->
        val planted = plantedCrops.find { it.id == cropId }
        val crop = planted?.crop
        if (crop != null) {
            Div({
                classes(
                    "fixed",
                    "inset-0",
                    "bg-black/60",
                    "backdrop-blur-sm",
                    "flex",
                    "items-center",
                    "center",
                    "justify-center",
                    "z-[300]",
                    "p-4",
                    "overflow-y-auto"
                )
                onClick { showViewCropId = null }
            }) {
                Div({
                    classes(
                        "bg-white",
                        "rounded-[2rem]",
                        "max-w-4xl",
                        "w-full",
                        "my-8",
                        "relative",
                        "shadow-2xl",
                        "flex",
                        "flex-col",
                        "overflow-hidden"
                    )
                    onClick { it.stopPropagation() }
                }) {
                    // Header
                    Div({
                        classes(
                            "p-8",
                            "border-b",
                            "border-gray-100",
                            "flex",
                            "items-center",
                            "justify-between",
                            "bg-emerald-50/30"
                        )
                    }) {
                        Div({ classes("flex", "items-center", "gap-6") }) {
                            Div({
                                classes(
                                    "w-20",
                                    "h-20",
                                    "bg-white",
                                    "rounded-2xl",
                                    "flex",
                                    "items-center",
                                    "justify-center",
                                    "shadow-sm",
                                    "border",
                                    "border-emerald-100"
                                )
                            }) {
                                val icon = getCropImageSource(crop.icon)
                                if (icon != null) Img(
                                    src = icon,
                                    attrs = { classes("w-14", "h-14", "object-contain") })
                                else I({
                                    classes(
                                        "fas",
                                        "fa-leaf",
                                        "text-emerald-600",
                                        "text-3xl"
                                    )
                                })
                            }
                            Div {
                                H2({
                                    classes(
                                        "text-3xl",
                                        "font-extrabold",
                                        "text-gray-900",
                                        "mb-1"
                                    )
                                }) { Text(crop.name) }
                                P({
                                    classes(
                                        "text-emerald-600",
                                        "font-bold",
                                        "flex",
                                        "items-center",
                                        "gap-2"
                                    )
                                }) {
                                    I({ classes("fas", "fa-expand-arrows-alt") })
                                    Text("${crop.spacingBetweenPlants} x ${crop.spacingBetweenRows} cm")
                                }
                            }
                        }
                        Button({
                            classes(
                                "w-12",
                                "h-12",
                                "rounded-full",
                                "bg-white",
                                "text-gray-400",
                                "hover:text-gray-600",
                                "hover:bg-gray-100",
                                "transition-all",
                                "flex",
                                "items-center",
                                "justify-center",
                                "shadow-sm",
                                "border",
                                "border-gray-100"
                            )
                            onClick { showViewCropId = null }
                        }) { I({ classes("fas", "fa-times", "text-xl") }) }
                    }

                    // Content
                    Div({ classes("p-8", "overflow-y-auto", "max-h-[70vh]", "bg-white") }) {
                        Div({ classes("grid", "grid-cols-1", "lg:grid-cols-3", "gap-8") }) {
                            // Quick Stats Sidebar
                            Div({ classes("lg:col-span-1", "space-y-4") }) {
                                Div({
                                    classes(
                                        "p-6",
                                        "bg-gray-50/50",
                                        "rounded-3xl",
                                        "border",
                                        "border-gray-100",
                                        "space-y-4"
                                    )
                                }) {
                                    QuickInfoItem(
                                        "Latinski naziv",
                                        crop.latinName ?: "-",
                                        "fa-italic"
                                    )
                                    QuickInfoItem(
                                        "Visina",
                                        if (crop.height != null) "${crop.height} cm" else "-",
                                        "fa-arrows-alt-v"
                                    )
                                    QuickInfoItem(
                                        "Resursi", when (crop.resourceConsumption?.lowercase()) {
                                            "high" -> "Visoka"; "medium" -> "Srednja"; "low" -> "Niska"; else -> "-"
                                        }, "fa-chart-pie"
                                    )
                                    QuickInfoItem(
                                        "Zalijevanje", when (crop.waterRequirements?.lowercase()) {
                                            "high" -> "Često"; "medium" -> "Umjereno"; "low" -> "Rijetko"; else -> "-"
                                        }, "fa-tint"
                                    )
                                }

                                if (crop.isCompanion) {
                                    Div({
                                        classes(
                                            "p-6",
                                            "bg-purple-50",
                                            "border-2",
                                            "border-purple-100",
                                            "rounded-3xl",
                                            "flex",
                                            "items-start",
                                            "gap-4"
                                        )
                                    }) {
                                        I({
                                            classes(
                                                "fas",
                                                "fa-hands-helping",
                                                "text-purple-500",
                                                "text-xl",
                                                "mt-1"
                                            )
                                        })
                                        Div {
                                            Span({
                                                classes(
                                                    "font-bold",
                                                    "text-purple-900",
                                                    "block",
                                                    "mb-1"
                                                )
                                            }) { Text("Biljka pratilac") }
                                            P({
                                                classes(
                                                    "text-xs",
                                                    "text-purple-700",
                                                    "leading-relaxed"
                                                )
                                            }) {
                                                Text("Specijalna biljka koja pomaže glavnim kulturama kroz zaštitu od nametnika ili poboljšanje rasta.")
                                            }
                                        }
                                    }
                                }
                            }

                            // Description & Details
                            Div({ classes("lg:col-span-2", "space-y-8") }) {
                                if (!crop.description.isNullOrBlank()) {
                                    Div {
                                        H3({
                                            classes(
                                                "text-lg",
                                                "font-bold",
                                                "text-gray-900",
                                                "mb-3",
                                                "flex",
                                                "items-center",
                                                "gap-2"
                                            )
                                        }) {
                                            I({
                                                classes(
                                                    "fas",
                                                    "fa-info-circle",
                                                    "text-emerald-500"
                                                )
                                            })
                                            Text("O kulturi")
                                        }
                                        P({
                                            classes(
                                                "text-gray-600",
                                                "leading-relaxed",
                                                "bg-gray-50/30",
                                                "p-4",
                                                "rounded-2xl",
                                                "border",
                                                "border-gray-100"
                                            )
                                        }) {
                                            Text(crop.description)
                                        }
                                    }
                                }

                                // Requirements Grid
                                Div {
                                    H3({
                                        classes(
                                            "text-lg",
                                            "font-bold",
                                            "text-gray-900",
                                            "mb-4"
                                        )
                                    }) { Text("Uslovi uzgoja") }
                                    Div({ classes("grid", "grid-cols-2", "gap-4") }) {
                                        SmallRequirementCard(
                                            "Mraz", when (crop.frostSensitivity?.lowercase()) {
                                                "sensitive" -> "Osjetljivo"; "hardy" -> "Otporno"; else -> "N/A"
                                            }, "fa-snowflake", "text-cyan-600", "bg-cyan-50"
                                        )
                                        SmallRequirementCard(
                                            "Optimalna T",
                                            if (crop.optimalTemperatureMin != null) "${crop.optimalTemperatureMin}-${crop.optimalTemperatureMax}°C" else "N/A",
                                            "fa-thermometer-half",
                                            "text-orange-600",
                                            "bg-orange-50"
                                        )
                                        SmallRequirementCard(
                                            "Min. T",
                                            if (crop.minTemperature != null) "${crop.minTemperature}°C" else "N/A",
                                            "fa-temperature-low",
                                            "text-indigo-600",
                                            "bg-indigo-50"
                                        )
                                        SmallRequirementCard(
                                            "Resursi",
                                            when (crop.resourceConsumption?.lowercase()) {
                                                "high" -> "Visoka"; "medium" -> "Srednja"; "low" -> "Niska"; else -> "N/A"
                                            },
                                            "fa-chart-pie",
                                            "text-emerald-600",
                                            "bg-emerald-50"
                                        )
                                    }
                                }

                                // Companions Tags
                                if (crop.goodCompanions.isNotEmpty() || crop.badCompanions.isNotEmpty()) {
                                    Div({ classes("space-y-4") }) {
                                        if (crop.goodCompanions.isNotEmpty()) {
                                            Div {
                                                Span({
                                                    classes(
                                                        "text-[10px]",
                                                        "font-black",
                                                        "text-emerald-600",
                                                        "uppercase",
                                                        "tracking-wider",
                                                        "block",
                                                        "mb-2"
                                                    )
                                                }) { Text("Dobri susjedi") }
                                                Div({ classes("flex", "flex-wrap", "gap-2") }) {
                                                    crop.goodCompanions.forEach {
                                                        Span({
                                                            classes(
                                                                "px-3",
                                                                "py-1.2",
                                                                "bg-emerald-50",
                                                                "text-emerald-700",
                                                                "text-xs",
                                                                "font-bold",
                                                                "rounded-full",
                                                                "border",
                                                                "border-emerald-100"
                                                            )
                                                        }) { Text(it.name) }
                                                    }
                                                }
                                            }
                                        }
                                        if (crop.badCompanions.isNotEmpty()) {
                                            Div {
                                                Span({
                                                    classes(
                                                        "text-[10px]",
                                                        "font-black",
                                                        "text-red-500",
                                                        "uppercase",
                                                        "tracking-wider",
                                                        "block",
                                                        "mb-2"
                                                    )
                                                }) { Text("Loši susjedi") }
                                                Div({ classes("flex", "flex-wrap", "gap-2") }) {
                                                    crop.badCompanions.forEach {
                                                        Span({
                                                            classes(
                                                                "px-3",
                                                                "py-1.2",
                                                                "bg-red-50",
                                                                "text-red-600",
                                                                "text-xs",
                                                                "font-bold",
                                                                "rounded-full",
                                                                "border",
                                                                "border-red-100"
                                                            )
                                                        }) { Text(it.name) }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    if (showAddCropModal) {
        val fillingFoil = fillingFoilId?.let { fid -> plantedCrops.find { it.id == fid } }
        val holePos = targetHolePosition

        // Find the foil that owns this hole if we clicked a specific hole
        val foilOwner = holePos?.let { pos ->
            plantedCrops.find { foil ->
                if (foil.crop?.icon != "strawberry_foil") return@find false

                val isHorizontal = foil.positionX == 0 && bed.orientation != "1"
                val fWidth = if (isHorizontal) bed.width else (foil.customWidth ?: 50)
                val fHeight = if (isHorizontal) (foil.customHeight ?: 50) else bed.length

                val actualHoleX = pos.first
                val actualHoleY = pos.second

                actualHoleX >= foil.positionX && actualHoleX <= foil.positionX + fWidth &&
                        actualHoleY >= foil.positionY && actualHoleY <= foil.positionY + fHeight
            }
        }

        AddCropModal(
            bedWidth = bed.width,
            availableCrops = availableCrops,
            isFillingFoil = fillingFoil != null || holePos != null,
            onClose = {
                showAddCropModal = false
                targetHolePosition = null
                fillingFoilId = null
            },
            onSelect = { cropSelect: CropData ->
                val hPos = targetHolePosition
                val currentFillingFoilId = fillingFoilId

                if (currentFillingFoilId != null || hPos != null) {
                    val targetFoil = if (currentFillingFoilId != null) {
                        plantedCrops.find { it.id == currentFillingFoilId }
                    } else {
                        foilOwner
                    }

                    if (targetFoil != null) {
                        // Remove any existing crops in this foil (Replace logic)
                        plantedCrops.filter { it.foilId == targetFoil.id }.forEach {
                            onRemoveCrop(it.id)
                        }

                        // Add only ONE crop with foilId - position doesn't matter since it will be rendered on all holes
                        onAddCrop(cropSelect, 0, 0, targetFoil.id)
                    }

                    fillingFoilId = null
                    targetHolePosition = null
                } else {
                    selectedCropForPlacement = cropSelect
                }
                showAddCropModal = false
            }
        )
    }

    if (showAiModal) {
        AiSelectionModal(
            availableCrops = availableCrops,
            onClose = { showAiModal = false },
            onGenerate = { crops, pathways, width ->
                onGenerateAi(crops, pathways, width)
                showAiModal = false
            }
        )
    }

    // Delete All Confirmation Modal
    if (showDeleteAllModal) {
        Div({
            classes(
                "fixed",
                "inset-0",
                "bg-black/50",
                "flex",
                "items-center",
                "justify-center",
                "z-[100]",
                "p-4"
            )
            onClick { showDeleteAllModal = false }
        }) {
            Div({
                classes(
                    "bg-white",
                    "rounded-xl",
                    "shadow-2xl",
                    "p-6",
                    "max-w-md",
                    "w-full"
                )
                onClick { it.stopPropagation() }
            }) {
                // Icon
                Div({ classes("flex", "justify-center", "mb-4") }) {
                    Div({
                        classes(
                            "w-16",
                            "h-16",
                            "bg-red-100",
                            "rounded-full",
                            "flex",
                            "items-center",
                            "justify-center"
                        )
                    }) {
                        I({ classes("fas", "fa-exclamation-triangle", "text-red-600", "text-2xl") })
                    }
                }

                // Title
                H3({
                    classes("text-xl", "font-bold", "text-gray-900", "text-center", "mb-2")
                }) {
                    Text("Obriši sve kulture?")
                }

                // Message
                P({
                    classes("text-gray-600", "text-center", "mb-6")
                }) {
                    Text("Da li ste sigurni da želite obrisati sve zasađene kulture iz ove leje? Ova akcija se ne može poništiti.")
                }

                // Buttons
                Div({ classes("flex", "gap-3") }) {
                    Button({
                        classes(
                            "flex-1",
                            "px-4",
                            "py-3",
                            "bg-gray-200",
                            "text-gray-700",
                            "rounded-lg",
                            "font-semibold",
                            "hover:bg-gray-300",
                            "transition-colors"
                        )
                        onClick { showDeleteAllModal = false }
                    }) {
                        Text("Odustani")
                    }

                    Button({
                        classes(
                            "flex-1",
                            "px-4",
                            "py-3",
                            "bg-red-600",
                            "text-white",
                            "rounded-lg",
                            "font-semibold",
                            "hover:bg-red-700",
                            "transition-colors"
                        )
                        onClick {
                            plantedCrops.forEach { onRemoveCrop(it.id) }
                            showDeleteAllModal = false
                        }
                    }) {
                        Text("Obriši sve")
                    }
                }
            }
        }
    }
}

@Composable
fun AddCropModal(
    bedWidth: Int,
    availableCrops: List<CropData>,
    isFillingFoil: Boolean = false,
    onClose: () -> Unit,
    onSelect: (CropData) -> Unit
) {
    Div({
        classes(
            "fixed",
            "inset-0",
            "bg-black/50",
            "flex",
            "items-center",
            "justify-center",
            "z-[100]",
            "p-4"
        )
        onClick { onClose() }
    }) {
        Div({
            classes(
                "bg-white",
                "rounded-3xl",
                "max-w-4xl",
                "w-full",
                "max-h-[90vh]",
                "flex",
                "flex-col",
                "shadow-2xl"
            )
            onClick { it.stopPropagation() }
        }) {
            Div({
                classes(
                    "p-6",
                    "border-b",
                    "border-gray-100",
                    "flex",
                    "justify-between",
                    "items-center"
                )
            }) {
                H2({
                    classes("text-2xl", "font-bold", "text-gray-900")
                }) { Text(if (isFillingFoil) "Popuni foliju" else "Odaberite kulturu za dodavanje") }
                Button({
                    classes("text-gray-400", "hover:text-gray-600"); onClick { onClose() }
                }) { I({ classes("fas", "fa-times", "text-xl") }) }
            }
            var activeModalTab by remember { mutableStateOf(0) }
            val tabs = if (isFillingFoil) listOf("Glavno", "Pratioci") else listOf("Glavno", "Pratioci", "Staze")
            Div({ classes("flex", "px-6", "pt-4", "gap-8", "border-b", "border-gray-100") }) {
                tabs.forEachIndexed { index, label ->
                    val isActive = activeModalTab == index
                    Div({
                        classes("pb-4", "font-bold", "cursor-pointer", "relative", "transition-all")
                        if (isActive) classes("text-emerald-600") else classes(
                            "text-gray-400",
                            "hover:text-gray-600"
                        )
                        onClick { activeModalTab = index }
                    }) {
                        Text(label)
                        if (isActive) Div({
                            classes(
                                "absolute",
                                "bottom-0",
                                "left-0",
                                "right-0",
                                "h-1",
                                "bg-emerald-600",
                                "rounded-t-full"
                            )
                        })
                    }
                }
            }
            Div({ classes("p-6", "overflow-y-auto", "flex-1") }) {
                when (activeModalTab) {
                    0 -> {
                        val mainCrops = availableCrops.filter { !it.isCompanion && it.id != -1 }
                        CropGrid(mainCrops, onSelect)
                    }

                    1 -> {
                        val companionCrops = availableCrops.filter { it.isCompanion }
                        CropGrid(companionCrops, onSelect)
                    }

                    2 -> {
                        PathSelectionView(bedWidth, onSelect)
                    }
                }
            }
        }
    }
}

@Composable
fun PathSelectionView(bedWidth: Int, onSelect: (CropData) -> Unit) {
    var fullWidthPath by remember { mutableStateOf(false) }

    H4({
        classes(
            "text-sm",
            "font-bold",
            "text-amber-900",
            "mb-3",
            "uppercase",
            "tracking-wider"
        )
    }) { Text("Zemljane staze") }
    Div({ classes("grid", "grid-cols-2", "sm:grid-cols-4", "gap-4", "mb-8") }) {
        listOf(20, 30, 40, 50).forEach { size ->
            Div({
                classes(
                    "p-6",
                    "bg-white",
                    "border-2",
                    "border-gray-100",
                    "rounded-2xl",
                    "hover:border-amber-500",
                    "hover:bg-amber-50",
                    "transition-all",
                    "cursor-pointer",
                    "text-center",
                    "group"
                )
                onClick {
                    val pathCrop = CropData(
                        id = -1,
                        name = "Staza ${size}x${size}",
                        spacingBetweenPlants = size,
                        spacingBetweenRows = bedWidth,
                        icon = "pathway"
                    )
                    onSelect(pathCrop)
                }
            }) {
                Div({
                    classes(
                        "w-12",
                        "h-12",
                        "bg-[#8B4513]",
                        "rounded-md",
                        "mx-auto",
                        "mb-3",
                        "group-hover:scale-110",
                        "transition-transform"
                    )
                })
                Span({
                    classes(
                        "font-bold",
                        "text-gray-800",
                        "block"
                    )
                }) { Text(if (fullWidthPath) "Širina leje x ${size}cm" else "${size} x ${size} cm") }
                Span({ classes("text-xs", "text-gray-400") }) { Text("Smeđa staza") }
            }
        }
    }

    H4({
        classes(
            "text-sm",
            "font-bold",
            "text-gray-900",
            "mb-3",
            "uppercase",
            "tracking-wider"
        )
    }) { Text("Crna folija (Jagode - 2 reda) širina 50cm") }
    Div({ classes("grid", "grid-cols-2", "sm:grid-cols-4", "gap-4", "mb-6") }) {
        listOf(30, 40, 50, 60).forEach { spacing ->
            Div({
                classes(
                    "p-6",
                    "bg-white",
                    "border-2",
                    "border-gray-100",
                    "rounded-2xl",
                    "hover:border-emerald-500",
                    "hover:bg-emerald-50",
                    "transition-all",
                    "cursor-pointer",
                    "text-center",
                    "group"
                )
                onClick {
                    val foilCrop = CropData(
                        id = -2,
                        name = "Crna folija (razmak ${spacing}cm) - 2 reda",
                        spacingBetweenPlants = spacing,
                        spacingBetweenRows = bedWidth,
                        icon = "strawberry_foil",
                        holeSpacing = spacing,
                        rows = 2
                    )
                    onSelect(foilCrop)
                }
            }) {
                Div({
                    classes(
                        "w-12",
                        "h-12",
                        "bg-gray-900",
                        "rounded-md",
                        "mx-auto",
                        "mb-3",
                        "group-hover:scale-110",
                        "transition-transform",
                        "flex",
                        "items-center",
                        "justify-around",
                        "px-1"
                    )
                }) {
                    Div({ classes("w-2", "h-2", "bg-gray-700", "rounded-full") })
                    Div({ classes("w-2", "h-2", "bg-gray-700", "rounded-full") })
                }
                Span({
                    classes(
                        "font-bold",
                        "text-gray-800",
                        "block"
                    )
                }) { Text("Razmak ${spacing}cm") }
                Span({ classes("text-xs", "text-gray-400") }) { Text("Puna širina folije") }
            }
        }
    }

    H4({
        classes(
            "text-sm",
            "font-bold",
            "text-gray-900",
            "mb-3",
            "uppercase",
            "tracking-wider"
        )
    }) { Text("Crna folija (Jagode - 1 red) širina 50cm") }
    Div({ classes("grid", "grid-cols-2", "sm:grid-cols-4", "gap-4") }) {
        listOf(30, 40, 50, 60).forEach { spacing ->
            Div({
                classes(
                    "p-6",
                    "bg-white",
                    "border-2",
                    "border-gray-100",
                    "rounded-2xl",
                    "hover:border-emerald-500",
                    "hover:bg-emerald-50",
                    "transition-all",
                    "cursor-pointer",
                    "text-center",
                    "group"
                )
                onClick {
                    val foilCrop = CropData(
                        id = -3,
                        name = "Crna folija (razmak ${spacing}cm) - 1 red",
                        spacingBetweenPlants = spacing,
                        spacingBetweenRows = bedWidth,
                        icon = "strawberry_foil",
                        holeSpacing = spacing,
                        rows = 1
                    )
                    onSelect(foilCrop)
                }
            }) {
                Div({
                    classes(
                        "w-12",
                        "h-12",
                        "bg-gray-900",
                        "rounded-md",
                        "mx-auto",
                        "mb-3",
                        "group-hover:scale-110",
                        "transition-transform",
                        "flex",
                        "items-center",
                        "justify-around",
                        "px-1"
                    )
                }) {
                    Div({ classes("w-2", "h-2", "bg-gray-700", "rounded-full") })
                }
                Span({
                    classes(
                        "font-bold",
                        "text-gray-800",
                        "block"
                    )
                }) { Text("Razmak ${spacing}cm") }
                Span({ classes("text-xs", "text-gray-400") }) { Text("Puna širina folije") }
            }
        }
    }
}

@Composable
fun QuickInfoItem(label: String, value: String, icon: String) {
    Div({ classes("flex", "items-center", "gap-3") }) {
        Div({
            classes(
                "w-8",
                "h-8",
                "bg-white",
                "rounded-lg",
                "flex",
                "items-center",
                "justify-center",
                "text-gray-400",
                "border",
                "border-gray-100"
            )
        }) {
            I({ classes("fas", icon, "text-sm") })
        }
        Div {
            Span({
                classes(
                    "text-[10px]",
                    "text-gray-400",
                    "block",
                    "leading-none"
                )
            }) { Text(label) }
            Span({ classes("text-sm", "font-bold", "text-gray-700") }) { Text(value) }
        }
    }
}

@Composable
fun SmallRequirementCard(
    title: String,
    value: String,
    icon: String,
    textColor: String,
    bgColor: String
) {
    Div({
        classes(
            "p-4",
            bgColor,
            "rounded-2xl",
            "flex",
            "items-center",
            "gap-3",
            "border",
            "border-transparent"
        )
    }) {
        I({ classes("fas", icon, textColor, "text-lg") })
        Div {
            Span({
                classes(
                    "text-[10px]",
                    "text-gray-500",
                    "block",
                    "leading-none",
                    "mb-0.5"
                )
            }) { Text(title) }
            Span({ classes("font-black", "text-gray-800", "text-sm") }) { Text(value) }
        }
    }
}

@Composable
fun CropGrid(
    crops: List<CropData>,
    onSelect: (CropData) -> Unit
) {
    if (crops.isEmpty()) {
        Div({ classes("text-center", "py-12", "text-gray-400") }) {
            I({
                classes(
                    "fas",
                    "fa-leaf",
                    "text-4xl",
                    "mb-2",
                    "block"
                )
            }); Text("Nema dostupnih kultura.")
        }
    } else {
        Div({ classes("grid", "grid-cols-2", "sm:grid-cols-3", "md:grid-cols-4", "gap-4") }) {
            crops.forEach { crop ->
                Div({
                    classes(
                        "p-4", "bg-white", "border", "border-gray-100", "rounded-2xl",
                        "hover:border-emerald-500", "hover:bg-emerald-50", "transition-all",
                        "cursor-pointer", "flex", "flex-col", "items-center", "gap-3"
                    )
                    onClick { onSelect(crop) }
                }) {
                    Div({
                        classes(
                            "w-12",
                            "h-12",
                            "bg-white",
                            "rounded-xl",
                            "flex",
                            "items-center",
                            "justify-center",
                            "flex-shrink-0"
                        )
                    }) {
                        val icon = getCropImageSource(crop.icon)
                        if (icon != null) Img(
                            src = icon,
                            attrs = { classes("w-10", "h-10", "object-contain") })
                        else I({ classes("fas", "fa-leaf", "text-emerald-600", "text-xl") })
                    }
                    Div({ classes("text-center") }) {
                        Span({ classes("text-sm", "font-bold", "text-gray-800", "block") }) {
                            Text(
                                crop.name
                            )
                        }
                        Span({
                            classes(
                                "text-[10px]",
                                "text-gray-400"
                            )
                        }) { Text("${crop.spacingBetweenPlants}x${crop.spacingBetweenRows}cm") }
                    }
                }
            }
        }
    }
}
